<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <section v-if="loadData" class="widget-section q-mb-lg">
      <EnvRegistration
        :registrationId="registrationId"
        :registrationData="registrationData"
        :envs="envs"
        :existEnvironments="environments"
        mode="edit"
      />
    </section>
    <section v-if="loadData" class="widget-section q-mb-lg">
      <q-banner
        v-if="!environments.find((env) => env.value === 'PROD')"
        rounded
        style="min-height: 40px"
        class="bg-info text-white q-mb-md"
      >
        Please register <b>PROD</b> environment for dns records creation.
      </q-banner>
      <div class="flex items-center">
        <span ref="dns-records" class="label-block">DNS Records:</span>
        <q-btn
          flat
          no-caps
          class="q-ml-auto btn--no-hover text-weight-regular"
          round
          color="light-blue-5"
          size="15px"
          :disabled="createDnsError?.length || showCreateForm"
          style="min-height: 30px"
          :ripple="false"
          @click="showCreateDnsForm('external')"
        >
          + Add external DNS record
          <q-tooltip v-if="createDnsError?.length">{{ createDnsError }}</q-tooltip>
        </q-btn>
        <span class="q-mx-md">/</span>
        <q-btn
          flat
          no-caps
          class="btn--no-hover text-weight-regular"
          round
          color="light-blue-5"
          size="15px"
          :disable="Boolean(createInternalDnsError?.length) || showCreateForm"
          style="min-height: 30px"
          :ripple="false"
          @click="showCreateDnsForm('internal')"
        >
          + Add internal DNS record
          <q-tooltip v-if="createInternalDnsError?.length">{{ createInternalDnsError }}</q-tooltip>
        </q-btn>
      </div>
      <div v-if="showCreateForm">
        <div class="update-form">
          <q-banner
            v-for="(message, key) in prevalMessages"
            :key="`preval-${key}`"
            class="text-white q-mb-sm"
            :class="bannerClass(message.severity)"
          >
            <span class="flex items-center">
              <q-icon :name="message.severity === 'warning' ? 'warning' : 'info'" class="q-mr-xs" size="16px"></q-icon>
              {{ message.message }}
            </span>
          </q-banner>
          <q-banner v-if="updateGeneralErrors.length" class="text-white approval-error-banner q-mb-sm">
            <span class="flex items-center" v-for="(error, key) in updateGeneralErrors" :key="key">
              <q-icon name="error" class="q-mr-xs" size="16px"></q-icon>{{ error.message }}
            </span>
          </q-banner>
          <div class="flex full-width no-wrap">
            <JsonForms
              :renderers="renderers"
              :schema="
                workFlowAction === 'engprt.create-network-external-dns' ? createDnsSchema : createInternalDnsSchema
              "
              :data="formData"
              :uischema="dnsUiSchema"
              :additionalErrors="updateErrors"
              validationMode="ValidateAndShow"
              @change="validateForm"
            />
            <q-btn
              color="primary"
              size="md"
              class="q-ml-sm"
              :loading="loadSend"
              :disable="
                loadSend ||
                submitDisable ||
                Boolean(formErrors.length) ||
                Boolean(updateErrors.length) ||
                Boolean(updateGeneralErrors.length)
              "
              style="height: 38px"
              @click="sendData()"
              no-caps
              label="Submit"
            ></q-btn>

            <q-btn flat size="md" class="btn--no-hover" style="height: 40px" no-caps @click="closeEditForm"
              >Cancel</q-btn
            >
          </div>
        </div>
      </div>
      <DnsRecords
        :registrationData="registrationData"
        :registrationId="registrationId"
        :expanded="expanded"
        mode="edit"
        @toggleExpanded="toggleExpanded($event)"
        @confirmDeleteDnsRecord="confirmDeleteDnsRecord($event)"
        @reassign="openReassignDialog($event)"
      >
        <template #expand>
          <q-td colspan="100%" class="update-form-cell">
            <q-banner
              v-for="(message, key) in prevalMessages"
              :key="`expanded-preval-${key}`"
              class="text-white q-mb-sm"
              :class="bannerClass(message.severity)"
            >
              <span class="flex items-center">
                <q-icon :name="message.severity === 'warning' ? 'warning' : 'info'" class="q-mr-xs" size="16px"></q-icon>
                {{ message.message }}
              </span>
            </q-banner>
            <q-banner v-if="updateGeneralErrors.length" class="text-white approval-error-banner">
              <span class="flex items-center" v-for="(error, key) in updateGeneralErrors" :key="key">
                <q-icon name="error" class="q-mr-xs" size="16px"></q-icon>{{ error.message }}
              </span>
            </q-banner>
            <div class="flex full-width no-wrap update-form q-pb-sm q-pt-sm">
              <JsonForms
                :renderers="renderers"
                :schema="updateDnsSchema"
                :data="formData"
                :uischema="dnsUiSchema"
                :additionalErrors="updateErrors"
                validationMode="ValidateAndShow"
                @change="validateForm"
              />
              <q-btn
                color="primary"
                size="md"
                class="q-ml-sm"
                :loading="loadSend"
                :disable="
                  loadSend ||
                  submitDisable ||
                  Boolean(formErrors.length) ||
                  Boolean(updateErrors.length) ||
                  Boolean(updateGeneralErrors.length)
                "
                style="height: 38px"
                @click="sendData()"
                no-caps
                label="Submit"
              ></q-btn>

              <q-btn flat size="md" class="btn--no-hover" style="height: 40px" no-caps @click="closeEditForm"
                >Cancel</q-btn
              >
            </div>
          </q-td>
        </template>
      </DnsRecords>
    </section>

    <section v-if="loadData && uiConfig?.showFirewallRules" class="widget-section q-mb-lg">
      <div class="row justify-end">
        <a
          target="_blank"
          href="https://wiki.swissre.com/pages/viewpage.action?spaceKey=CMP&title=How+to+understand+Firewall+Rules+Change+Management+process"
          >Wiki</a
        >
        <span class="q-mx-xs"> / </span>
        <a
          target="_blank"
          href="https://swissreesm.service-now.com/now/nav/ui/classic/params/target/incident.do?sys_id=-1&sysparm_query=assignment_group%3D42e8fb4783a69a542d0198747daad335%5eassigned_to%3De6cbabc80a0a3c2c0126655b64aac8ca%5eshort_description%3DFirewall%2520rule%2520creation:%2520new%2520EAR%2520process%2520help%5ecategory%3DIncident%5ecmdb_ci%3D462a8a14870f2400600893531a434d48^sysparm_default_value_caller_id=$%7Bgs.getUserID()%7D"
          >I need help</a
        >
      </div>
      <div class="flex items-center q-mb-md">
        <span ref="firewall-rules" class="label-block">Firewall Rule (EAR) Requests:</span>
        <q-btn
          flat
          no-caps
          class="q-ml-auto btn--no-hover text-weight-regular"
          round
          :disabled="Boolean(createFirewallError?.length) || showCreateFirewallRulesForm"
          color="light-blue-5"
          size="15px"
          style="min-height: 30px"
          :ripple="false"
          @click="showCreateFirewallRules"
        >
          + Request Firewall Rule (EAR)
          <q-tooltip v-if="createFirewallError?.length">{{ createFirewallError }}</q-tooltip>
        </q-btn>
      </div>
      <div v-if="showCreateFirewallRulesForm">
        <q-banner class="bg-blue text-white q-mb-md" style="min-height: 30px; border-radius: 4px">
          Using this form, you can create Firewall Rule (EAR) request <b>from</b> or <b>to</b> the current digital service
          <b>({{ registrationData.digitalServiceApm }})</b>.
        </q-banner>
        <div class="update-form">
          <q-banner
            v-for="(message, key) in prevalMessages"
            :key="`firewall-preval-${key}`"
            class="text-white q-mb-sm"
            :class="bannerClass(message.severity)"
          >
            <span class="flex items-center">
              <q-icon :name="message.severity === 'warning' ? 'warning' : 'info'" class="q-mr-xs" size="16px"></q-icon>
              {{ message.message }}
            </span>
          </q-banner>
          <q-banner v-if="updateGeneralErrors.length" class="text-white approval-error-banner q-mb-sm">
            <span class="flex items-center" v-for="(error, key) in updateGeneralErrors" :key="key">
              <q-icon name="error" class="q-mr-xs" size="16px"></q-icon>{{ error.message }}
            </span>
          </q-banner>
          <div class="row q-pl-sm">
            <div class="flex items-center q-ml-auto q-mr-lg">
              <span
                >Simplified (segments)<q-icon class="q-mb-md" name="info">
                  <q-tooltip
                    >Choose from available Connectivity Matrix segment (Location > Name > Environment > APM)</q-tooltip
                  >
                </q-icon></span
              >
              <q-toggle
                @update:model-value="resetError"
                keep-color
                class="custom-toggle"
                :dark="false"
                color="primary"
                v-model="isAdvanced"
              >
                <q-tooltip>When switching, all input data will be lost</q-tooltip>
              </q-toggle>
              <span
                >Advanced<q-icon class="q-mb-md" name="info">
                  <q-tooltip>Enter Source and Destination IP address(es) (single or multiple IP/CIDR)</q-tooltip>
                </q-icon></span
              >
            </div>
          </div>
          <WorkflowForm
            v-if="!isAdvanced"
            workflowId="engprt.update-firewall-rules-segments"
            :commonFormData="formData"
            :registrationData="registrationData"
            :cancelButton="cancelButtonHandler"
          >
            <template #toolbarContent>
              <b class="q-ml-auto q-mr-lg block"
                >Need any help? Please contact support by creating a
                <a
                  target="_blank"
                  href="https://swissreesm.service-now.com/now/nav/ui/classic/params/target/incident.do?sys_id=-1&sysparm_query=assignment_group%3D42e8fb4783a69a542d0198747daad335%5eassigned_to%3De6cbabc80a0a3c2c0126655b64aac8ca%5eshort_description%3DFirewall%2520rule%2520creation:%2520new%2520EAR%2520process%2520help%5ecategory%3DIncident%5ecmdb_ci%3D462a8a14870f2400600893531a434d48^sysparm_default_value_caller_id=$%7Bgs.getUserID()%7D"
                  >ticket</a
                ></b
              >
            </template>
          </WorkflowForm>

          <WorkflowForm
            v-if="isAdvanced"
            workflowId="engprt.update-firewall-rules-advanced"
            :commonFormData="formData"
            :registrationData="registrationData"
            :cancelButton="cancelButtonHandler"
            :editData="cloneFirewallRule"
          >
            <template #toolbarContent>
              <b class="q-ml-auto q-mr-lg block"
                >Need any help? Please contact support by creating a
                <a
                  target="_blank"
                  href="https://swissreesm.service-now.com/now/nav/ui/classic/params/target/incident.do?sys_id=-1&sysparm_query=assignment_group%3D42e8fb4783a69a542d0198747daad335%5eassigned_to%3De6cbabc80a0a3c2c0126655b64aac8ca%5eshort_description%3DFirewall%2520rule%2520creation:%2520new%2520EAR%2520process%2520help%5ecategory%3DIncident%5ecmdb_ci%3D462a8a14870f2400600893531a434d48^sysparm_default_value_caller_id=$%7Bgs.getUserID()%7D"
                  >ticket</a
                ></b
              >
            </template>
          </WorkflowForm>
        </div>
      </div>
      <EarWidget
        :registrationData="registrationData"
        :registrationId="registrationId"
        earType="source"
        mode="edit"
        @duplicate="duplicateRecord($event, 'source')"
      />
      <EarWidget
        :registrationData="registrationData"
        :registrationId="registrationId"
        earType="target"
        mode="edit"
        @duplicate="duplicateRecord($event, 'destination')"
      />
    </section>
  </q-page>

  <q-dialog v-model="deleteDnsConfirm" class="single-service-page" persistent>
    <q-card style="min-width: 768px; border-radius: 0px">
      <q-toolbar class="shadow-2 q-py-sm q-px-md">
        <q-toolbar-title class="text-weight-bold">
          <strong>Confirm {{ currentDnsRecord?.scope }} DNS deletion</strong>
        </q-toolbar-title>
        <q-btn icon="close" round flat class="btn--no-hover" @click="deleteDnsConfirm = false"></q-btn>
      </q-toolbar>
      <q-card-section v-if="deleteDnsMessages.length" class="q-pb-none">
        <q-banner
          v-for="(message, key) in deleteDnsMessages"
          :key="`delete-dns-${key}`"
          class="text-white q-mb-md"
          :class="bannerClass(message.severity)"
        >
          <span class="flex items-center">
            <q-icon :name="message.severity === 'warning' ? 'warning' : 'info'" class="q-mr-xs" size="16px"></q-icon>
            {{ message.message }}
          </span>
        </q-banner>
      </q-card-section>
      <q-card-actions class="q-pb-md">
        <q-btn
          color="primary"
          :disabled="delelteDnsLoading"
          :loading="delelteDnsLoading"
          @click="deleteDnsRecord"
          size="md"
          class="q-ml-sm"
          no-caps
          label="Yes"
        ></q-btn>
        <q-btn flat size="md" class="btn--no-hover" style="height: 40px" no-caps @click="deleteDnsConfirm = false"
          >No</q-btn
        >
      </q-card-actions>
    </q-card>
  </q-dialog>
  <ReassignDialog
    :showReassignDialog="showReassignDialog"
    entity="external-dns"
    :newDigitalService="newDigitalService"
    :loading="loadReassign"
    :errors="reassignErrors"
    @submit="sendReassignRequest"
    @cancel="closeReassignDialog"
    @resetService="resetDigitalService"
    @setService="setDigitalService"
  />
</template>

<script>
import WorkflowForm from '../workflow-form/WorkflowForm.vue';
import { JsonForms } from '@jsonforms/vue';
import { scroll } from 'quasar';
import emitter from 'tiny-emitter/instance';
import { quasarRenderers } from '@/shared/jsonformsQuasarRenderers/renderers';
import * as uiConfig from '@/shared/ui-config.json';
import {
  getRegistration,
  validateWorkFlow,
  createWorkFlow,
  getBluePrint,
  subValWorkflow,
  getSegments,
  extractPrevalMessages,
} from '../../api/api';
import EnvRegistration from '../EnvRegistration.vue';
import ReassignDialog from '../ReassignDialog.vue';
import DnsRecords from '@/pages/digitalService/components/network/DnsRecords.vue';
import EarWidget from '@/pages/digitalService/components/ear/EarWidget.vue';
import { firewallRulesSchemaAdapter } from '@/pages/registrations/components/workflow-form/adapters';

const renderers = [...quasarRenderers];
const { getScrollTarget, setVerticalScrollPosition } = scroll;

export default {
  name: 'RegistrationNetwork',
  components: {
    JsonForms,
    EnvRegistration,
    ReassignDialog,
    DnsRecords,
    EarWidget,
    WorkflowForm,
  },
  props: {
    registrationId: {
      type: String,
    },
    registrationData: {
      type: Object,
    },
  },
  data() {
    return {
      isAdvanced: false,
      uiConfig: {},
      envs: ['PROD', 'NONPROD', 'DEV'],
      renderers: Object.freeze(renderers),
      createDnsSchema: {},
      createInternalDnsSchema: {},
      updateDnsSchema: {},
      cloneFirewallRule: {},
      dnsUiSchema: {
        type: 'Group',
        elements: [
          {
            type: 'HorizontalLayout',
            elements: [
              {
                type: 'Control',
                label: 'Scope',
                scope: '#/properties/scope',
                options: {
                  editable: false,
                },
                rule: {
                  effect: 'DISABLE',
                  condition: {
                    scope: '#/properties/scope',
                    schema: {
                      enum: ['external', 'internal'],
                    },
                  },
                },
              },
              {
                type: 'Control',
                label: 'Type',
                scope: '#/properties/type',
                options: {
                  editable: false,
                },
              },
              {
                type: 'Control',
                label: 'Name',
                scope: '#/properties/name',
                options: {
                  editable: false,
                },
              },
              {
                type: 'Control',
                label: 'Domain',
                scope: '#/properties/domain',
                options: {
                  editable: false,
                },
              },
              {
                type: 'Control',
                label: 'Value',
                scope: '#/properties/value',
                options: {
                  editable: true,
                },
                rule: {
                  effect: 'ENABLE',
                  condition: {
                    scope: '#/properties/type',
                    schema: {
                      enum: ['A', 'AAAA', 'CNAME', 'TXT', 'NS', 'MX', 'CNAME-WITH-REDIRECT', 'SRV'],
                    },
                  },
                },
              },
            ],
          },
          {
            type: 'HorizontalLayout',
            elements: [
              {
                type: 'Control',
                label: 'Custom Implementation schedule',
                scope: '#/properties/changeDetails/properties/enableDates',
                class: 'col-3',
              },
              {
                type: 'CustomDateControl',
                label: 'Start date',
                scope: '#/properties/changeDetails/properties/startDate',
                class: 'col-3',
                rule: {
                  effect: 'SHOW',
                  condition: {
                    scope: '#/properties/changeDetails/properties/enableDates',
                    schema: {
                      const: true,
                    },
                  },
                },
              },
              {
                type: 'CustomDateControl',
                label: 'End date',
                scope: '#/properties/changeDetails/properties/endDate',
                class: 'col-3',
                rule: {
                  effect: 'SHOW',
                  condition: {
                    scope: '#/properties/changeDetails/properties/enableDates',
                    schema: {
                      const: true,
                    },
                  },
                },
              },
            ],
          },
        ],
      },
      showCreateFirewallRulesForm: false,
      firewallRulesSchemaAdvanced: {},
      firewallRulesSchemaSegments: {},
      expanded: [],
      envTab: undefined,
      environments: [],
      delelteDnsLoading: false,
      updatedDnsRecord: {},
      updateErrors: [],
      formErrors: [],
      updateGeneralErrors: [],
      prevalMessages: [],
      formData: {
        digitalServiceApmId: this.registrationData.digitalServiceApm,
      },
      loadSend: false,
      submitDisable: true,
      deleteDnsConfirm: false,
      showCreateForm: false,
      showCreateInternalDnsForm: false,
      validationTimeout: false,
      workFlowAction: undefined,
      currentDnsRecord: undefined,
      deleteDnsMessages: [],
      createDnsError: undefined,
      createInternalDnsError: undefined,
      createFirewallError: undefined,
      loadData: false,
      showReassignDialog: false,
      newDigitalService: undefined,
      loadReassign: false,
      reassignErrors: undefined,
      allSegments: {},
    };
  },
  methods: {
    cancelButtonHandler() {
      this.closeEditForm();
    },
    bannerClass(severity) {
      return severity === 'warning' ? 'bg-orange-8' : 'bg-info';
    },
    pushError(path, message) {
      if (!this.updateErrors.find((e) => e.instancePath === path)) {
        this.updateErrors.push({
          instancePath: path,
          message,
          schemaPath: '',
          keyword: '',
          params: {},
        });
      }
    },
    removeError(path) {
      const index = this.updateErrors.findIndex((e) => e.instancePath === path);
      if (index >= 0) this.updateErrors.splice(index, 1);
    },
    resetError() {
      this.updateErrors = [];
      this.updateGeneralErrors = [];
      this.prevalMessages = [];
    },
    async validateEarForm(data) {
      Object.assign(this.formData, data);
      if (this.isAdvanced) {
        this.workFlowAction = 'engprt.update-firewall-rules-advanced';
      } else {
        this.workFlowAction = 'engprt.update-firewall-rules-segments';
      }
      this.runWorkflowValidation();
    },
    async runWorkflowValidation() {
      this.loadSend = true;
      try {
        const workFlowData = {
          workflowId: this.workFlowAction,
          inputVariables: this.formData,
        };
        const response = await validateWorkFlow(workFlowData);
        if (this.updateErrors.length) {
          this.updateErrors = [];
        }

        this.updateGeneralErrors = [];
        this.prevalMessages = extractPrevalMessages(response);
        this.submitDisable = false;
      } catch (error) {
        this.prevalMessages = [];
        if (error?.response?.data?.errors?.length) {
          //this.updateErrors = error.response.data.errors.filter((e) => e.instancePath.length);
          this.updateGeneralErrors = [{ message: error.message }] || [];
        } else {
          this.$notify('negative', 'Error', error.message);
          this.updateGeneralErrors = [{ message: error.message }] || [];
          if (this.updateErrors.length) {
            this.updateErrors = [];
          }
        }
      } finally {
        this.loadSend = false;
      }
    },
    async sendReassignRequest() {
      this.loadReassign = true;
      const data = {
        workflowId: 'engprt.move-network-external-dns',
        inputVariables: {
          newApmId: this.newDigitalService.apmId,
          id: this.currentDnsRecord.id,
        },
      };
      createWorkFlow(data)
        .then(() => {
          this.$notify(
            'positive',
            'Success',
            `DNS record will be reassign to ${this.newDigitalService.digitalServiceName}.`,
          );
          emitter.emit('reloadDnsTable');
        })
        .catch((error) => {
          this.$notify('negative', 'Error', error.message);
        })
        .finally(() => {
          this.closeReassignDialog();
        });
    },
    resetDigitalService() {
      this.newDigitalService = undefined;
      this.reassignErrors = undefined;
    },
    async setDigitalService(service) {
      this.reassignErrors = undefined;
      this.loadReassign = true;
      this.newDigitalService = service;
      const data = {
        workflowId: `engprt.move-network-${this.currentDnsRecord.scope}-dns`,
        inputVariables: {
          newApmId: this.newDigitalService.apmId,
          id: this.currentDnsRecord.id,
        },
      };
      validateWorkFlow(data)
        .then(() => {
          this.reassignErrors = undefined;
        })
        .catch((error) => {
          this.reassignErrors = error.message;
        })
        .finally(() => {
          this.loadReassign = false;
        });
    },
    closeReassignDialog() {
      this.newDigitalService = undefined;
      this.currentDnsRecord = undefined;
      this.showReassignDialog = false;
      this.reassignErrors = undefined;
    },
    openReassignDialog(dns) {
      this.currentDnsRecord = dns;
      this.showReassignDialog = true;
    },
    showCreateFirewallRules() {
      //this.closeEditForm();
      this.showCreateFirewallRulesForm = true;
    },
    scrollToItem(anchor) {
      const element = this.$refs[anchor];
      if (!element) return;
      const target = getScrollTarget(element);
      const offset = element.offsetTop - (this.currentBanner ? 95 : 45);
      const duration = 1000;
      setVerticalScrollPosition(target, offset, duration);
    },
    duplicateRecord(rule, type) {
      this.closeEditForm();
      this.scrollToItem('firewall-rules');
      this.isAdvanced = true;
      this.cloneFirewallRule = firewallRulesSchemaAdapter(rule, type);
      this.showCreateFirewallRules();
    },
    showCreateDnsForm(scope) {
      this.workFlowAction = `engprt.create-network-${scope}-dns`;
      this.dnsUiSchema.elements[0].elements[0].options.default = scope;
      this.closeEditForm();
      this.expanded = [];
      this.showCreateForm = true;
    },
    async confirmDeleteDnsRecord(dns) {
      this.currentDnsRecord = dns;
      const deleteDnsWorkflow = {
        workflowId: `engprt.delete-network-${dns.scope}-dns`,
        inputVariables: {
          digitalServiceApmId: this.registrationData.digitalServiceApm,
          id: dns.id,
        },
      };
      try {
        const response = await validateWorkFlow(deleteDnsWorkflow);
        this.deleteDnsMessages = extractPrevalMessages(response);
        this.deleteDnsConfirm = true;
      } catch (error) {
        this.deleteDnsMessages = [];
        this.$notify('negative', 'Error', error.message);
        this.currentDnsRecord = undefined;
      }
    },
    async deleteDnsRecord() {
      this.delelteDnsLoading = true;
      const deleteDnsWorkflow = {
        workflowId: `engprt.delete-network-${this.currentDnsRecord.scope}-dns`,
        inputVariables: {
          digitalServiceApmId: this.registrationData.digitalServiceApm,
          id: this.currentDnsRecord.id,
        },
      };
      try {
        await createWorkFlow(deleteDnsWorkflow);
        this.$notify('positive', 'Success', 'DNS record will be deleted.');
        emitter.emit('reloadDnsTable');
        emitter.emit('reloadEnvironments');
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.delelteDnsLoading = false;
        this.currentDnsRecord = undefined;
        this.deleteDnsMessages = [];
        this.deleteDnsConfirm = false;
      }
    },
    closeEditForm() {
      if (this.validationTimeout) clearTimeout(this.validationTimeout);
      this.showCreateForm = false;
      this.showCreateInternalDnsForm = false;
      this.showCreateFirewallRulesForm = false;
      this.formData = {
        digitalServiceApmId: this.registrationData.digitalServiceApm,
      };
      this.expanded = [];
      this.formErrors = [];
      this.updateErrors = [];
      this.updateGeneralErrors = [];
      this.prevalMessages = [];
      this.cloneFirewallRule = {};
      this.submitDisable = true;
    },
    async sendData() {
      try {
        const workFlowData = {
          workflowId: this.workFlowAction,
          inputVariables: this.formData,
        };
        this.loadSend = true;
        await createWorkFlow(workFlowData);
        if (
          this.workFlowAction === 'engprt.create-network-external-dns' ||
          this.workFlowAction === 'engprt.update-network-external-dns' ||
          this.workFlowAction === 'engprt.create-network-internal-dns' ||
          this.workFlowAction === 'engprt.update-network-internal-dns'
        ) {
          this.$notify(
            'positive',
            'Success',
            `DNS record will be ${
              this.workFlowAction === 'engprt.create-network-external-dns' ||
              this.workFlowAction === 'engprt.create-network-internal-dns'
                ? 'created'
                : 'updated'
            }.`,
          );
          emitter.emit('reloadDnsTable');
          emitter.emit('reloadEnvironments');
        }
        if (this.workFlowAction.includes('engprt.update-firewall-rules')) {
          this.$notify('positive', 'Success', 'Creation of Firewall Rule (EAR) request has started.');
          emitter.emit('reloadFirewallRules');
          emitter.emit('reloadEnvironments');
        }
        this.closeEditForm();
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loadSend = false;
      }
    },
    validateForm(value) {
      if (value.data?.changeDetails?.startDate && value.data?.changeDetails?.endDate) {
        if (new Date(value.data?.changeDetails?.startDate) > new Date(value.data?.changeDetails?.endDate)) {
          this.pushError('/changeDetails/endDate', 'End date must be later than start date.');
        } else {
          this.removeError('/changeDetails/endDate');
        }
      }
      this.formErrors = value.errors;
      if (!value.data?.changeDetails?.enableDates) {
        if (value.data?.changeDetails) {
          delete value.data.changeDetails.startDate;
          delete value.data.changeDetails.endDate;
        }
      }
      this.formData = value.data;
      if (!value.errors.length && !this.updateErrors.length) {
        if (this.validationTimeout) clearTimeout(this.validationTimeout);
        this.validationTimeout = setTimeout(() => this.runWorkflowValidation(), 2000);
      } else {
        this.submitDisable = true;
      }
    },
    toggleExpanded(row) {
      this.formErrors = [];
      this.updateErrors = [];
      this.updateGeneralErrors = [];
      this.prevalMessages = [];
      this.showCreateForm = false;
      this.workFlowAction = `engprt.update-network-${row.scope}-dns`;
      const { id, name, type, scope, domain, value } = row;
      Object.assign(this.formData, { id, name, type, scope, domain, value, changeDetails: { enableDates: false } });
      this.expanded = this.expanded[0] === row.id ? [] : [row.id];
    },
  },
  async mounted() {
    this.allSegments = await getSegments();
    this.uiConfig = uiConfig[process.env.VUE_APP_ENV];
    const { inputVariablesSchema: updateSchema } = await getBluePrint('engprt.update-network-external-dns');
    delete updateSchema.$async;
    delete updateSchema.$schema;
    this.updateDnsSchema = updateSchema;
    const { inputVariablesSchema: createSchema } = await getBluePrint('engprt.create-network-external-dns');
    delete createSchema.$async;
    delete createSchema.$schema;
    this.createDnsSchema = createSchema;
    const { inputVariablesSchema: createInternalDnsSchema } = await getBluePrint('engprt.create-network-internal-dns');
    delete createInternalDnsSchema.$async;
    delete createInternalDnsSchema.$schema;
    this.createInternalDnsSchema = createInternalDnsSchema;
    const { inputVariablesSchema: firewallRulesSchemaAdvanced } = await getBluePrint(
      'engprt.update-firewall-rules-advanced',
    );
    delete firewallRulesSchemaAdvanced.$async;
    delete firewallRulesSchemaAdvanced.$schema;
    this.firewallRulesSchemaAdvanced = firewallRulesSchemaAdvanced;
    const { inputVariablesSchema: firewallRulesSchemaSegments } = await getBluePrint(
      'engprt.update-firewall-rules-segments',
    );
    delete firewallRulesSchemaSegments.$async;
    delete firewallRulesSchemaSegments.$schema;
    this.firewallRulesSchemaSegments = firewallRulesSchemaSegments;
    this.loadData = false;
    this.$showLoading();
    const dataPromise = getRegistration(this.registrationId, true, false).then(async (response) => {
      this.environments = response.environments.map((env) => ({ status: env.status, value: env.value, id: env.id }));
    });
    const subValData = {
      inputVariables: {
        digitalServiceApmId: this.registrationData.digitalServiceApm,
      },
      subSchemaId: 'add-button',
    };
    const subvalCreateDns = {
      workflowId: 'engprt.create-network-external-dns',
      ...subValData,
    };
    const subvalCreateInternalDns = {
      workflowId: 'engprt.create-network-external-dns',
      ...subValData,
    };
    const subValCreateFirewallRule = {
      workflowId: 'engprt.update-firewall-rules-advanced',
      ...subValData,
    };
    const subvalDnsPromise = subValWorkflow(subvalCreateDns)
      .then(() => {
        this.createDnsError = undefined;
      })
      .catch((error) => {
        this.createDnsError = error.message;
      });
    const subvalInternalDnsPromise = subValWorkflow(subvalCreateInternalDns)
      .then(() => {
        this.createInternalDnsError = undefined;
      })
      .catch((error) => {
        this.createInternalDnsError = error.message;
      });
    const subvalFirewallRulePromise = subValWorkflow(subValCreateFirewallRule)
      .then(() => {
        this.createFirewallError = undefined;
      })
      .catch((error) => {
        this.createFirewallError = error.message;
      });
    try {
      await Promise.all([subvalDnsPromise, subvalInternalDnsPromise, subvalFirewallRulePromise, dataPromise]);
    } finally {
      this.$hideLoading();
      this.loadData = true;
      if (this.$route.query.tab) {
        this.$nextTick(() => {
          this.scrollToItem(this.$route.query.tab);
        });
      }
    }
  },
};
</script>

<style lang="scss">
.external-dns-table {
  .q-table__top {
    padding: 0 4px 0;
    background-color: #edeff5;
  }
  thead tr:first-child {
    height: 30px;

    th {
      height: 20px;
      text-transform: uppercase;
      background-color: #edeff5;
      color: #a29a9a;
      font-size: 10px;
    }
  }
}
.update-form-cell {
  .q-banner {
    min-height: 38px;
    margin-bottom: 5px;
  }
}
.update-form {
  background-color: var(--q-accent);
  padding: 18px 6px 10px;
  margin-bottom: 15px;
  border-top: 1px solid $grey-4;
  border-bottom: 1px solid $grey-4;
  .q-field__bottom {
    padding: 3px 0 0 3px;
    white-space: pre;
  }
}
</style>
<style lang="scss" scoped>
.env-form {
  width: 50%;
}
.widget-section {
  border-radius: 15px;
  background-color: white;
  padding: 30px;
}
.label-block {
  font-size: 20px;
  font-weight: bold;
}

.environments {
  ul {
    display: flex;
    margin: 0 -10px;
    padding: 0;
  }

  li {
    margin: 0 10px;

    i {
      margin-right: 5px;
    }

    display: flex;
    align-items: center;
  }
}

.widget-section {
  border-radius: 15px;
  background-color: white;
  padding: 30px;
}
</style>

<style lang="scss">
.custom-toggle .q-toggle__thumb::after {
  background-color: var(--q-primary) !important; /* checked track */
}
.custom-toggle .q-toggle__inner--truthy .q-toggle__track {
  opacity: 0.38;
}
</style>
